import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Chip, Button, IconButton,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Divider, Alert, Paper, Tabs, Tab, useMediaQuery, useTheme, Card, CardContent, Grid, Stack
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    ViewList as EntriesIcon,
    History as HistoryIcon,
    Assignment,
} from '@mui/icons-material';
import TimesheetVersionHistory from '../../timesheets/TimesheetVersionHistory';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { getUserData, getUserRole } from '../../../utils/auth';

const cap = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

// Display-only relabel — the backend/audit log uses "L1"/"L2" internally,
// but employees know these as "Internal"/"External" review.
const formatActivityAction = (action) => {
    if (!action) return '-';
    return action.replace(/\bL2\b/g, 'External').replace(/\bL1\b/g, 'Internal');
};

const STATUS_STYLES = {
    DRAFT:           { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED:       { bg: '#e3f2fd', color: '#1976d2' },
    APPROVED:        { bg: '#E0F2F1', color: '#00796B' },
    REJECTED:        { bg: '#FFEBEE', color: '#D32F2F' },
    L1_APPROVED:     { bg: '#f5f3ff', color: '#7c3aed' },
    PENDING_LEVEL_2: { bg: '#fff7ed', color: '#c2410c' },
};

const ACTIVITY_STATUS_STYLES = {
    DRAFT:           { bg: '#f5faffff', border_color: '#a1b9dbff' },
    SUBMITTED:       { bg: '#f4faffff', border_color: '#a6c5e4ff' },
    RESUBMITTED:     { bg: '#eff6ff', border_color: '#93c5fd' },
    APPROVED:        { bg: '#eefdf8ff', border_color: '#00a846ff' },
    REJECTED:        { bg: '#ffeeefff', border_color: '#dd5b51ff' },
    L1_APPROVED:     { bg: '#f9f4ffff', border_color: '#bf89faff' },
    PENDING_LEVEL_2: { bg: '#fff7ed', border_color: '#d1c0baff' },
};

const L2_CFG = {
    PENDING:      { label: 'Pending',  bg: '#fff7ed', color: '#c2410c', dot: '#f59e0b' },
    APPROVED:     { label: 'Approved', bg: '#f0fdf4', color: '#166534', dot: '#22c55e' },
    REJECTED:     { label: 'Rejected', bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
    NOT_REQUIRED: null,
};

const BASE_COLUMNS = ['Date', 'Day', 'Project', 'Task', 'Description', 'Hours', 'Billable', 'Location', 'OT Hrs', 'Remarks'];

function SummaryRow({ label, value, highlight }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1,
                bgcolor: highlight ? '#f0fdf4' : 'transparent',
                borderRadius: 1,
            }}
        >
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: highlight ? '#059669' : '#1e293b' }}>
                {value} hrs
            </Typography>
        </Box>
    );
}

// onRestore(entries) — called when employee restores a previous version on a DRAFT timesheet
function TimesheetDetails({ open, timesheet, onClose, onRestore }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState(0);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [globalConfig, setGlobalConfig] = useState(null);
    const [activities, setActivities] = useState([])
    const user = getUserData()
    const userId = user?.id;

    useEffect(() => {
        getData(API_ENDPOINTS.getTimesheetConfig())
            .then(res => setGlobalConfig(res?.result?.data ?? res?.result ?? null))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if(!timesheet) return;
        (async () => {
            try {
                const role = (getUserRole() || '').toLowerCase().trim();
                const isSupervisor = role === 'team lead' || role === 'manager';
                const projectBase = isSupervisor
                    ? API_ENDPOINTS.fetchSupervisorProjects
                    : API_ENDPOINTS.fetchEmployeeProjects;
                const url = `${projectBase}/${userId}`;
                const resP = await getData(url);
                if (resP?.status === "SUCCESS")
                    setProjects(resP.message.data || []);
                const resT = await getData(
                    API_ENDPOINTS.getEmployeeTasks(userId),
                );
                if (resT?.status === "SUCCESS")
                    setTasks(resT.result.tasks || []);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [timesheet])

    const handleActivitiesList = async () => {
        try {
            const response = await getData(API_ENDPOINTS.getTimesheetAuditTrail(timesheet.id));
            if (response.status === 'SUCCESS') {
                const data = response?.result?.data;
                setActivities(Array.isArray(data) ? data : data ? [data] : []);
            }
        } catch (error) {
            console.log(error?.response?.data?.message || error);
        }
    };

    const formatIST = (timestampStr) => {
        if (!timestampStr) return { date: '-', time: '-' };
        const d = new Date(timestampStr);
        if (isNaN(d.getTime())) return { date: '-', time: '-' };
        const date = d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Kolkata' });
        const time = d.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
        return { date, time };
    };

    if (!timesheet) return null;

    const style = STATUS_STYLES[timesheet.status] || STATUS_STYLES.DRAFT;
    const totalNonBillable = timesheet.total_hours - timesheet.total_billable_hours;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const s = new Date(timesheet.week_start_date);
    const e = new Date(timesheet.week_end_date);
    const periodLabel = `${monthNames[s.getMonth()]} ${s.getDate()} – ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;

    // const hasVersionHistory = timesheet.version && timesheet.version.length > 0;  // Phase 2

    // L2 helpers — only show L2 info when two-level approval is globally enabled
    const twoLevelEnabled = !!globalConfig?.is_two_level_approval;
    const hasL2 = twoLevelEnabled && timesheet.entries.some(entry => entry.l2_status && entry.l2_status !== 'NOT_REQUIRED');
    const l2RejectedEntries = twoLevelEnabled ? timesheet.entries.filter(entry => entry.l2_status === 'REJECTED') : [];
    const columns = hasL2 ? [...BASE_COLUMNS, 'Ext. Status'] : BASE_COLUMNS;

    const handleClose = () => {
        setActiveTab(0);
        onClose();
        setActivities([]);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" fullScreen={isMobile} PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Timesheet Details
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {periodLabel}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                        label={
                            timesheet.status === 'PENDING_LEVEL_2' ? 'Pending External Approval' :
                            timesheet.status
                        }
                        size="small"
                        sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 12, borderRadius: 2 }}
                    />
                    <IconButton onClick={handleClose} size="small" sx={{ color: '#64748b' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            {/* Tab bar */}
            <Box sx={{ px: 3, borderBottom: '1px solid #e2e8f0' }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44 },
                        '& .Mui-selected': { color: '#6366f1' },
                        '& .MuiTabs-indicator': { bgcolor: '#6366f1' },
                    }}
                >
                    <Tab icon={<EntriesIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Entries" />
                    <Tab onClick={handleActivitiesList} icon={<Assignment sx={{ fontSize: 16 }} />} iconPosition="start" label="Activities" />
                    {/* Phase 2 — Version History tab
                    <Tab
                        icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                        label={
                            hasVersionHistory
                                ? `Version History (${timesheet.version})`
                                : 'Version History'
                        }
                    />
                    */}
                </Tabs>
            </Box>

            <DialogContent sx={{ p: 3 }}>
                {/* ── Tab 0: Entries ── */}
                {activeTab === 0 && (
                    <>
                        {/* Approver info */}
                        {timesheet.approver_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PersonIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {timesheet.status === 'APPROVED' ? 'APPROVED' : 'Reviewed'} by{' '}
                                    <strong>{timesheet.approver_name}</strong>
                                </Typography>
                            </Box>
                        )}

                        {/* Pending Level 2 banner */}
                        {timesheet.status === 'PENDING_LEVEL_2' && (
                            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                                Some entries are awaiting external approval. You will be notified once all
                                entries are reviewed.
                            </Alert>
                        )}

                        {/* Rejection comment */}
                        {timesheet.status === 'REJECTED' && timesheet.rejection_reason && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                                    Rejection Reason
                                </Typography>
                                {cap(timesheet.rejection_reason)}
                            </Alert>
                        )}

                        {/* External approver comment */}
                        {timesheet.level2_comments && (
                            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                                    External Approver Comment
                                </Typography>
                                {cap(timesheet.level2_comments)}
                            </Alert>
                        )}

                        {/* L2 rejection details */}
                        {timesheet.status === 'REJECTED' && l2RejectedEntries.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                    The following entries were rejected by the external approver — please correct them before resubmitting:
                                </Typography>
                                {l2RejectedEntries.map((entry, i) => (
                                    <Box key={entry.id ?? i} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                                            {entry.entry_date}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                                            {entry.project_name || entry.project_id}{entry.l2_comments ? ` — ${cap(entry.l2_comments)}` : ''}
                                        </Typography>
                                    </Box>
                                ))}
                            </Alert>
                        )}

                        {/* Mobile Cards (Visible below md) */}
                        <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
                            {timesheet.entries.sort((a, b) => {
                                if (a.l2_status === 'APPROVED' && b.l2_status !== 'APPROVED') return -1;
                                if (a.l2_status !== 'APPROVED' && b.l2_status === 'APPROVED') return 1;
                                return 0;
                            }).map((row, idx) => {
                                const isL2Rejected = row.l2_status === 'REJECTED';
                                const l2cfg = row.l2_status ? L2_CFG[row.l2_status] : null;

                                return (() => {
                                    const isSimpleDay = (
                                        row.day_type === 'Holiday' ||
                                        row.day_type === 'Weekend' ||
                                        (row.day_type === 'Leave' && row.leave_type !== 'Work From Home')
                                    );

                                    return (
                                        <Card key={idx} variant="outlined" sx={{ borderRadius: 2, borderColor: isL2Rejected ? '#fecaca' : '#e2e8f0', bgcolor: isL2Rejected ? '#fff5f5' : '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{row.entry_date}</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                <Typography sx={{ fontSize: 12, color: '#64748b' }}>{row.day_type}</Typography>
                                                                {row.leave_type && <Typography sx={{ fontSize: 11, color: '#ca39f7' }}>({row.leave_type})</Typography>}
                                                            </Box>
                                                        </Box>
                                                        {!isSimpleDay && Number(row.hours_worked) > 0 && (
                                                            <Chip
                                                                label={row.is_billable ? 'Billable' : 'Non-Billable'}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: 10, height: 20,
                                                                    bgcolor: row.is_billable ? '#e0f2f1' : '#fce4ec',
                                                                    color: row.is_billable ? '#00796b' : '#c62828',
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {isSimpleDay ? (
                                                        row.remarks && (
                                                            <>
                                                                <Divider />
                                                                <Box>
                                                                    <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25 }}>Remarks</Typography>
                                                                    <Typography sx={{ fontSize: 13, color: '#64748b' }}>{row.remarks}</Typography>
                                                                </Box>
                                                            </>
                                                        )
                                                    ) : (
                                                        <>
                                                            <Divider />

                                                            <Stack spacing={1.5}>
                                                                {(row.day_type === 'Working' || (row.day_type === 'Leave' && row.leave_type === 'Work From Home')) && (
                                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                            <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25 }}>Project</Typography>
                                                                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.project_name || row.project_id}</Typography>
                                                                        </Box>
                                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                            <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25 }}>Task</Typography>
                                                                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.task_name || row.task_id}</Typography>
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25 }}>Description</Typography>
                                                                        <Typography sx={{ fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.work_description || '—'}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ flexShrink: 0, display: 'flex', gap: 1 }}>
                                                                        <Box>
                                                                            <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25 }}>Hours</Typography>
                                                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                                                                                {row.hours_worked}{Number(row.overtime_hours) > 0 ? ` (+${row.overtime_hours})` : ''}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box>
                                                                            <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25   }}>Location</Typography>
                                                                            <Typography sx={{ fontSize: 13, color: '#334155' }}>{row.location || '—'}</Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                                {row.remarks && (
                                                                    <Box>
                                                                        <Typography sx={{ fontSize: 11, color: '#94a3b8', mb: 0.25 }}>Remarks</Typography>
                                                                        <Typography sx={{ fontSize: 13, color: '#64748b' }}>{row.remarks}</Typography>
                                                                    </Box>
                                                                )}
                                                            </Stack>

                                                            {hasL2 && (row.day_type === 'Working' || (row.day_type === 'Leave' && row.leave_type === 'Work From Home')) && (
                                                                <Box sx={{ mt: 1, p: 1.5, bgcolor: isL2Rejected ? '#fff1f2' : '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: isL2Rejected ? '#fecaca' : '#e2e8f0' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>External Review</Typography>
                                                                        {l2cfg ? (
                                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: 10, bgcolor: l2cfg.bg }}>
                                                                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: l2cfg.dot, flexShrink: 0 }} />
                                                                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: l2cfg.color, lineHeight: 1 }}>
                                                                                    {l2cfg.label}
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                                                                        )}
                                                                    </Box>
                                                                    {isL2Rejected && row.l2_comments && (
                                                                        <Typography sx={{ fontSize: 11, color: '#991b1b', mt: 1, fontStyle: 'italic' }}>
                                                                            {cap(row.l2_comments)}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })();
                            })}
                        </Stack>

                        {/* Entry Table (Visible md and above) */}
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {columns.map((col) => (
                                            <TableCell
                                                key={col}
                                                sx={{ fontWeight: 700, fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}
                                            >
                                                {col}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {timesheet.entries.sort((a, b) => {
                                        if (a.l2_status === 'APPROVED' && b.l2_status !== 'APPROVED') return -1;
                                        if (a.l2_status !== 'APPROVED' && b.l2_status === 'APPROVED') return 1;
                                        return 0; // Keep original order for all other records
                                    }).map((row, idx) => {
                                        const isL2Rejected = row.l2_status === 'REJECTED';
                                        const l2cfg = row.l2_status ? L2_CFG[row.l2_status] : null;
                                        return (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    '&:last-child td': { borderBottom: 0 },
                                                    '&:hover': { bgcolor: isL2Rejected ? '#fff1f2' : '#f8fafc' },
                                                    ...(isL2Rejected ? { bgcolor: '#fff5f5' } : {}),
                                                }}
                                            >
                                                <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{row.entry_date}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography sx={{fontSize: 13}}>
                                                            {row.day_type}
                                                        </Typography>
                                                        {(row.day_type === 'Leave' && row.leave_type === 'Work From Home') &&
                                                            <Typography sx={{ fontSize: 11, color: '#ca39f7ff' }}>
                                                                (WFH)
                                                            </Typography>
                                                        }
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 13, maxWidth: 140 }}>{row.project_name || row.project_id}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>{row.task_name || row.task_id}</TableCell>
                                                <TableCell sx={{ fontSize: 13, maxWidth: 200 }}>{row.work_description || '—'}</TableCell>
                                                <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{row.hours_worked}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>
                                                    {Number(row.hours_worked) === 0 ? (
                                                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                                                    ) : (
                                                        <Chip
                                                            label={row.is_billable ? 'Billable' : 'Non-Billable'}
                                                            size="small"
                                                            sx={{
                                                                fontSize: 11,
                                                                height: 20,
                                                                bgcolor: row.is_billable ? '#e0f2f1' : '#fce4ec',
                                                                color: row.is_billable ? '#00796b' : '#c62828',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>{row.location || '—'}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>{row.overtime_hours || 0}</TableCell>
                                                <TableCell sx={{ fontSize: 13, color: '#64748b' }}>{row.remarks || '—'}</TableCell>
                                                {hasL2 && (
                                                    <TableCell sx={{ fontSize: 12 }}>
                                                        {l2cfg ? (
                                                            <>
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: 10, bgcolor: l2cfg.bg }}>
                                                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: l2cfg.dot, flexShrink: 0 }} />
                                                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: l2cfg.color, lineHeight: 1 }}>
                                                                        {l2cfg.label}
                                                                    </Typography>
                                                                </Box>
                                                                {isL2Rejected && row.l2_comments && (
                                                                    <Typography sx={{ fontSize: 10, color: '#991b1b', mt: 0.5, fontStyle: 'italic' }}>
                                                                        {cap(row.l2_comments)}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        ) : <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Summary */}
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                            <Box sx={{ bgcolor: '#f8fafc', px: 2, py: 1, borderBottom: '1px solid #e2e8f0' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                                    Weekly Summary
                                </Typography>
                            </Box>
                            <SummaryRow label="Total Regular Hours" value={Number(timesheet.total_hours).toFixed(2)} />
                            <SummaryRow label="Total Overtime Hours" value={Number(timesheet.total_overtime_hours).toFixed(2)} />
                            <SummaryRow label="Total Billable Hours" value={Number(timesheet.total_billable_hours).toFixed(2)} highlight />
                            <SummaryRow label="Total Non-Billable Hours" value={Number(totalNonBillable).toFixed(2)} />
                        </Box>

                        {/* Employee Comment */}
                        {timesheet.remarks && (
                            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mb: 0.5 }}>
                                    Employee Comment
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {timesheet.remarks}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                {/* Phase 2 — Version History tab content
                {activeTab === 2 && (
                    <TimesheetVersionHistory
                        versionHistory={timesheet.versionHistory}
                        canRestore={timesheet.status === 'DRAFT'}
                        onRestore={onRestore}
                    />
                )}
                */}
                {/* Tab 1: Activities */}
                {activeTab === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        {activities.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>No activities found.</Typography>
                            </Box>
                        ) : (
                            activities.map((activity, idx) => {
                                const { date, time } = formatIST(activity.timestamp);
                                const status = activity.action.includes('Approved') ? 'Approved': activity.action.includes('Rejected') ? 'Rejected': activity.action;
                                const statusIndex = status.toUpperCase().replaceAll(" ", "_");
                                const activityStyle = ACTIVITY_STATUS_STYLES[statusIndex] || ACTIVITY_STATUS_STYLES.DRAFT;
                                return (
                                    <Box
                                        key={idx}
                                        sx={{
                                            p: 2,
                                            bgcolor: activityStyle.bg,
                                            borderRadius: 3,
                                            border: `1px solid ${activityStyle.border_color}`,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {activity.comment && (
                                            <Box sx={{ pl: 1, borderLeft: `3px solid ${activityStyle.border_color}` , py: 0.3, mb: 0.5 }}>
                                                <Typography sx={{ color: '#5c6169ff', fontSize: 13, fontStyle: 'italic', fontWeight: 600 }}>
                                                    {activity.comment}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontWeight: 600, color: '#000000ff', fontSize: 14, mb: 1 }}>
                                                {formatActivityAction(activity.action)} <span style={{ fontWeight: 400 }}>by</span> {activity.actor || '-'}
                                            </Typography>
                                            <Typography sx={{ color: '#000000ff', fontSize: 12, fontWeight: 200 }}>
                                                {date} {time}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{ textTransform: 'none', borderRadius: 2, color: '#64748b', borderColor: '#e2e8f0' }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default TimesheetDetails;
